import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import toast from 'react-hot-toast';

/**
 * ADMIN — Bidder Overview. AdminRoute-gated (renders bidder PII). Mirrors the
 * SaleRegistrations / AdminConsignors pattern: read real collections, join, filter, table.
 *
 * The bidder population is the UNION of:
 *   - users with role == 'bidder' (the platform's bidder role; guests carry guestBidder=true), AND
 *   - anyone who appears as a bidderId in the `bids` collection
 * so that ANY account that has bid is guaranteed to appear (this is how you locate a
 * bidder account you can't otherwise identify). role is shown per row, since consignors
 * kept a 'bidder' role too.
 *
 * READ-ONLY overview. The per-bidder "Set bid limit" and "Ban" controls are STUBS —
 * the UI is present but they DO NOT write or enforce anything yet (by design: these are
 * consequential actions on real people; policy + enforcement are wired later).
 */

interface BidderRow {
  uid: string;
  name: string;
  email: string;
  bidderNumber: string;
  role: string;
  isGuest: boolean;
  createdAt: Date | null;
  bidCount: number;    // non-voided
  lotCount: number;    // distinct lots bid on (non-voided)
  lastBidAt: Date | null;
}

const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const shortUid = (uid: string) => (uid.length > 12 ? `${uid.slice(0, 6)}…${uid.slice(-4)}` : uid);

type SortKey = 'activity' | 'name' | 'registered';

const AdminBidders: React.FC = () => {
  const [rows, setRows] = useState<BidderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('activity');

  // STUB management modal — preview only, writes nothing.
  const [manage, setManage] = useState<{ row: BidderRow; mode: 'limit' | 'ban' } | null>(null);
  const [limitValue, setLimitValue] = useState('');
  const [banReason, setBanReason] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [usersSnap, bidsSnap] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'bids')),
        ]);
        if (!active) return;

        // Aggregate bid activity per bidder (voided/rejected excluded).
        const agg = new Map<string, { count: number; lots: Set<string>; last: number }>();
        bidsSnap.forEach((d) => {
          const b: any = d.data();
          if (!b.bidderId) return;
          if (b.status === 'rejected' || b.status === 'pending' || b.voidReason) return;
          const cur = agg.get(b.bidderId) ?? { count: 0, lots: new Set<string>(), last: 0 };
          cur.count += 1;
          if (b.lotId) cur.lots.add(b.lotId);
          const ms = b.placedAt?.toDate ? b.placedAt.toDate().getTime() : 0;
          if (ms > cur.last) cur.last = ms;
          agg.set(b.bidderId, cur);
        });

        // Index users, and build the bidder set = role 'bidder' ∪ anyone who has bid.
        const userById = new Map<string, any>();
        usersSnap.forEach((d) => userById.set(d.id, d.data()));

        const bidderUids = new Set<string>();
        userById.forEach((u, uid) => { if (u.role === 'bidder') bidderUids.add(uid); });
        agg.forEach((_v, uid) => bidderUids.add(uid));

        const out: BidderRow[] = Array.from(bidderUids).map((uid) => {
          const u = userById.get(uid) || {};
          const a = agg.get(uid);
          return {
            uid,
            name: u.displayName || '—',
            email: u.email || '—',
            bidderNumber: u.bidderNumber || '—',
            role: u.role || (userById.has(uid) ? 'unknown' : 'no account'),
            isGuest: u.guestBidder === true,
            createdAt: toDate(u.createdAt),
            bidCount: a?.count ?? 0,
            lotCount: a ? a.lots.size : 0,
            lastBidAt: a && a.last ? new Date(a.last) : null,
          };
        });
        setRows(out);
      } catch (e) {
        console.error('Failed to load bidders:', e);
        toast.error('Could not load bidders.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = rows.filter((r) =>
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.uid.toLowerCase().includes(q) ||
        String(r.bidderNumber).toLowerCase().includes(q)
      );
    }
    const sorted = [...list];
    if (sortKey === 'name') sorted.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortKey === 'registered') sorted.sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
    else sorted.sort((a, b) => (b.lastBidAt?.getTime() ?? 0) - (a.lastBidAt?.getTime() ?? 0) || b.bidCount - a.bidCount);
    return sorted;
  }, [rows, filter, sortKey]);

  const openManage = (row: BidderRow, mode: 'limit' | 'ban') => {
    setLimitValue('');
    setBanReason('');
    setManage({ row, mode });
  };

  // STUB: intentionally writes NOTHING. Enforcement is wired in a later, deliberate pass.
  const submitStub = () => {
    toast('Preview only — not enforced yet. Nothing was saved.', { icon: '🚧' });
    setManage(null);
  };

  const copyUid = async (uid: string) => {
    try { await navigator.clipboard.writeText(uid); toast.success('UID copied'); }
    catch { toast.error('Copy failed'); }
  };

  if (loading) return <div className="text-center py-12 text-gray-600">Loading bidders…</div>;

  const totalBidders = rows.length;
  const activeBidders = rows.filter((r) => r.bidCount > 0).length;
  const guestBidders = rows.filter((r) => r.isGuest).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Bidders ({totalBidders})</h1>
          <p className="text-gray-600">
            {activeBidders} have placed bids · {guestBidders} guest accounts. Read-only overview.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            className="input py-2"
            placeholder="Search name / email / UID / bidder #"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)} className="input py-2" aria-label="Sort bidders">
            <option value="activity">Sort: Recent activity</option>
            <option value="name">Sort: Name</option>
            <option value="registered">Sort: Newest account</option>
          </select>
        </div>
      </div>

      {/* Honesty banner: management is not live yet. */}
      <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-2 text-sm text-amber-900">
        🚧 <strong>Management is a preview.</strong> “Set bid limit” and “Ban” show the intended controls but do
        not enforce anything yet — no changes are saved. Enforcement will be wired in a later, deliberate pass.
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b bg-gray-50">
              <th className="py-2 px-2">Bidder #</th>
              <th className="py-2 px-2">Name</th>
              <th className="py-2 px-2">Email</th>
              <th className="py-2 px-2">Account (UID)</th>
              <th className="py-2 px-2">Registered</th>
              <th className="py-2 px-2 text-right">Bids</th>
              <th className="py-2 px-2 text-right">Lots</th>
              <th className="py-2 px-2">Last bid</th>
              <th className="py-2 px-2">Manage</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.uid} className="border-b last:border-0 hover:bg-gray-50 align-top">
                <td className="py-2 px-2 font-mono whitespace-nowrap">#{r.bidderNumber}</td>
                <td className="py-2 px-2 font-semibold">
                  {r.name}
                  <div className="flex gap-1 mt-0.5">
                    {r.isGuest && <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">GUEST</span>}
                    {r.role !== 'bidder' && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{r.role}</span>}
                  </div>
                </td>
                <td className="py-2 px-2 text-gray-700 break-all">{r.email}</td>
                <td className="py-2 px-2">
                  <button onClick={() => copyUid(r.uid)} title={`${r.uid} — click to copy`} className="font-mono text-xs text-gray-500 hover:text-primary-600">
                    {shortUid(r.uid)} 📋
                  </button>
                </td>
                <td className="py-2 px-2 text-gray-600 whitespace-nowrap">{r.createdAt ? r.createdAt.toLocaleDateString() : '—'}</td>
                <td className="py-2 px-2 text-right font-semibold">{r.bidCount}</td>
                <td className="py-2 px-2 text-right">{r.lotCount}</td>
                <td className="py-2 px-2 text-gray-600 whitespace-nowrap">{r.lastBidAt ? r.lastBidAt.toLocaleDateString() : '—'}</td>
                <td className="py-2 px-2">
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => openManage(r, 'limit')}
                      className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                      title="Set a bid limit (preview — not enforced yet)"
                    >
                      Set limit
                    </button>
                    <button
                      type="button"
                      onClick={() => openManage(r, 'ban')}
                      className="text-xs px-2 py-0.5 rounded border border-red-300 text-red-600 hover:bg-red-50"
                      title="Ban this bidder (preview — not enforced yet)"
                    >
                      Ban
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={9} className="py-6 text-center text-gray-500">No bidders match “{filter}”.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400">
        Showing {visible.length} of {totalBidders}. Bidder set = users with the bidder role plus anyone who has placed a bid.
      </p>

      {/* ---- STUB management modal (writes nothing) ---- */}
      {manage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold">
                {manage.mode === 'limit' ? 'Set bid limit' : 'Ban bidder'}
              </h2>
              <span className="text-[11px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded">PREVIEW · NOT ENFORCED</span>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {manage.row.name} · {manage.row.email}
            </p>

            {manage.mode === 'limit' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maximum total bid exposure (USD)</label>
                <input
                  type="number"
                  min="0"
                  inputMode="decimal"
                  placeholder="e.g. 5000"
                  value={limitValue}
                  onChange={(e) => setLimitValue(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Draft the policy here. When enforcement is wired, this cap will be checked at bid time.
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (internal)</label>
                <textarea
                  rows={3}
                  placeholder="Why is this bidder being banned?"
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
                <p className="text-xs text-red-600 mt-2">
                  Banning is a consequential action on a real person. This preview records nothing — enforcement
                  (blocking bids / registration) is wired later with an audit trail.
                </p>
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={submitStub}
                className={`flex-1 px-3 py-2 text-white text-sm rounded ${manage.mode === 'ban' ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-600 hover:bg-primary-700'}`}
              >
                {manage.mode === 'limit' ? 'Save limit' : 'Ban bidder'}
              </button>
              <button
                type="button"
                onClick={() => setManage(null)}
                className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBidders;
